<template>
  <v-app id="inspire" :style="appLayoutCssVars">
    <app-shell
      v-if="useAppShell"
      ref="appShell"
      :is-admin="isAdmin"
      :setup-progress="intelligenceSetupProgress"
      :show-setup-card="showIntelligenceSetupCard"
      :user-picture="userPicture"
      :right-drawer-open="right_drawer"
      @ask-tracey="goToTraceyPage"
      @whats-new="goToWhatsNew"
      @open-profile="openUtilityDrawer(1)"
      @open-utility-drawer="openUtilityDrawer"
      @close-utility-drawers="onSavedSearchesCloseUtilityDrawers"
      @continue-setup="onContinueIntelligenceSetup"
    >
      <template #search>
        <tender-search
          :header-state="searchHeaderState"
          @submit="$refs.advancedSearchDrawer && $refs.advancedSearchDrawer.submitFromHeader($event)"
          @add-keyword="$refs.advancedSearchDrawer && $refs.advancedSearchDrawer.addKeywordChip({ key: $event })"
          @remove-keyword="$refs.advancedSearchDrawer && $refs.advancedSearchDrawer.removeKeywordChip($event)"
          @search-focus="$refs.advancedSearchDrawer && $refs.advancedSearchDrawer.clickOpen()"
          @open-advanced-search="$refs.advancedSearchDrawer && $refs.advancedSearchDrawer.clickOpen()"
          @reset="$refs.advancedSearchDrawer && $refs.advancedSearchDrawer.resetFromHeader()"
          @keywords-overflow="$refs.advancedSearchDrawer && $refs.advancedSearchDrawer.openForKeywords()"
          @open-search-history="toggleSearchHistoryDrawer"
        />
      </template>

      <!-- Keep the Tracey page alive across navigation so an answer that is
           streaming keeps streaming (and is saved and charged in full) while
           the user is on another page. -->
      <keep-alive include="TraceyChatPage">
        <router-view />
      </keep-alive>
    </app-shell>

    <advanced-search-drawer
      ref="advancedSearchDrawer"
      :should-show="shouldShowAdvancedSearchDrawer"
      :right-drawer-open="right_drawer"
      @close-utility-drawers="onAdvancedSearchCloseUtilityDrawers"
      @filters-changed="onSearchHeaderFiltersChanged"
    />

    <help-drawer
      ref="helpDrawer"
      :should-show-help-drawer="shouldShowHelpDrawer"
      :right-drawer-open="right_drawer"
      @close-utility-drawers="onHelpCloseUtilityDrawers"
    />

    <search-history-drawer
      ref="searchHistoryDrawer"
      :should-show-search-history-drawer="shouldShowSearchHistoryDrawer"
      :right-drawer-open="right_drawer"
      @close-utility-drawers="onSearchHistoryCloseUtilityDrawers"
    />

    <v-navigation-drawer
      disable-resize-watcher
      v-model="right_drawer"
      right
      app
      temporary
      height="calc(100% - 24px)"
      :width="isDrawerFullScreen ? 'calc(100vw - 24px)' : '50vw'"
      :class="{ 'fullscreen-drawer': isDrawerFullScreen }"
      class="ma-3"
    >
      <v-card width="100%" height="100%">
        <v-tabs-items v-model="drawer_tabs" style="height: 100%">
          <v-tab-item :disabled="disabled()" style="height: 100%;">
            <div style="display: flex; flex-direction: column; height: 100%;">
              <v-card class=" font-weight-bold subtitle-1 py-4 px-4">
                <v-card-title class="pa-0 ma-0">
                  <span>
                    <v-badge color="secondary" offset-x="0" right small overlap>
                      <template v-slot:badge> <span class="text--black">Beta</span>
                      </template>
                      <!-- <v-icon medium class="pr-2">mdi-creation</v-icon> -->
                      Tracey
                    </v-badge>
                  </span>
                  <div class="flex-grow-1"></div>
                  <span>
                    <v-btn icon small @click="isDrawerFullScreen = !isDrawerFullScreen">
                      <v-icon>{{ isDrawerFullScreen ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
                    </v-btn>
                  </span>
                  <span>
                    <v-btn icon @click="right_drawer = false">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </span>
                </v-card-title>
                <span class="font-weight-normal caption">
                  Contract search assistant. Try the public beta for free.
                </span>
                <div>
                  <span @click="sendProductFeedback('Tracey')" class="text-capitalize font-weight-bold caption"
                    style="cursor: pointer;">
                    Share your feedback
                  </span>
                </div>
              </v-card>
              <v-divider></v-divider>
              <v-card flat tile class="flex-grow-1" style="min-height: 0">
                <tracey-chat-window :isFullScreen="isDrawerFullScreen"
                  @toggle-fullscreen="isDrawerFullScreen = !isDrawerFullScreen" />
              </v-card>
            </div>
          </v-tab-item>

          <v-tab-item :disabled="disabled()" class="profile-tab-item">
            <div class="profile-panel-layout">
              <v-card class="px-4 py-4 profile-panel-header" width="100%" flat tile>
                <div style="display: flex;">
                  <span>
                    <v-avatar size="36px" v-if="$auth.user.picture" class="pl-4 pr-7">
                      <img small :src="$auth.user.picture" alt="User's profile picture" />
                    </v-avatar>
                    <v-avatar size="36px" v-else color="primary" class="pr-7">
                      <v-icon dark>
                        mdi-account-circle
                      </v-icon>
                    </v-avatar>
                    <span class="title font-weight-bold subtitle-1">{{ $auth.user.name }}</span>
                  </span>
                  <div class="flex-grow-1"></div>
                  <span>
                    <v-btn icon @click="right_drawer = false">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </span>
                </div>
              </v-card>
              <v-divider></v-divider>
              <div class="profile-panel-mid">
                <user-profile />
              </div>
              <v-divider></v-divider>
              <div>
                <login :menu="login_menu" :authenticated="authenticated" @close="right_drawer = false" />
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-navigation-drawer>

    <router-view v-if="!useAppShell" />

    <v-dialog v-model="dialog" width="300">
      <v-card>
        <v-card-title class="headline" primary-title>
          {{ dialog_title }}
        </v-card-title>

        <v-card-text>
          {{ dialog_msg }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialog = false">
            Got It
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-app>
</template>

<script>
//import { METHODS } from "http";
import UserProfile from "./components/UserProfile.vue";
import Login from "./components/Login.vue";
//import MyLists from "./components/MyLists.vue";
//import ClusterMenu from "./components/ClusterMenu.vue";
//import ChatQueryWindow from "./components/ChatQueryWindow.vue";

import { bus } from "./main.js";
import { getInstance } from "./auth/index.js";
import { userLog, getCurrentUserProfile } from "./components/APIManager.js";
import TraceyChatWindow from "./components/TraceyChatWindow.vue";
import AppShell from "./components/layout/AppShell.vue";
import TenderSearch from "./components/TenderSearch.vue";
import AdvancedSearchDrawer from "./components/AdvancedSearchDrawer.vue";
import HelpDrawer from "./components/HelpDrawer.vue";
import SearchHistoryDrawer from "./components/SearchHistoryDrawer.vue";
import { isDashboardRoute } from "./config/appNavigation.js";
import { APP_HEADER_HEIGHT } from "./components/layout/navigationDrawerLayout.js";

export default {
  components: {
    UserProfile,
    Login,
    TraceyChatWindow,
    AppShell,
    TenderSearch,
    AdvancedSearchDrawer,
    HelpDrawer,
    SearchHistoryDrawer,
  },
  props: {
    source: String
  },
  data() {
    return {
      isDrawerFullScreen: false,
      showIndustryMenu: false,
      queryDialog: false,
      app_bar_active: false,
      tool_bar_active: true,
      current_tab: Number,
      drawer_tabs: null,
      right_drawer: false,
      recent_history_menu: true,
      my_lists_menu: true,
      login_menu: true,
      authenticated: true,
      disable_nav: false,
      dialog_msg: "",
      dialog: false,
      dialog_title: "",
      search_terms: "",
      nav_open: true,
      closeEntityMenu: false,
      overlay: true,
      category_list: [],
      supplier_list: ["test1", "test2"],
      competitor_list: [],
      currentUser: null,
      isAdmin: false,
      intelligenceSetupProgress: 0,
      showIntelligenceSetupCard: false,
      searchHeaderState: {
        keywordChips: [],
        hasAdvancedFilters: false,
        advancedFilterCount: 0,
      },
      //show_login_menu: false
    };
  },
  watch: {
    $route(to, from) {
      // Capture the full path of the route the user is navigating to
      const fullPath = window.location.origin + to.fullPath;
      this.$safePlausible.trackPageview({ url: to.fullPath });

      userLog(fullPath);
      this.handleDrawerOnRoute(to, from);
    },

    isDrawerFullScreen(val) {
      document.body.style.overflow = val ? 'hidden' : '';
    },
  },

  computed: {
    appTitle() {
      return this.$store.state.appTitle;
    },

    displayBar() {
      if (this.app_bar_active) {
        ////////////console.log("LOGGED IN")
        return { display: "none" };
      } else {
        return { display: "block" };
      }
    },

    shouldShowAppShell() {
      if (!this.authenticated) {
        return false;
      }
      if (this.$route.path.includes("/unsubscribe") || this.$route.path.includes("/not_found")) {
        return false;
      }
      if (this.$route.path.includes("/widgets/")) {
        return false;
      }
      return true;
    },

    useAppShell() {
      return this.app_bar_active && this.shouldShowAppShell;
    },

    shouldShowNavigationDrawer() {
      return this.shouldShowAppShell;
    },

    shouldShowAdvancedSearchDrawer() {
      return this.shouldShowAppShell;
    },

    shouldShowHelpDrawer() {
      return this.shouldShowAppShell;
    },

    shouldShowSearchHistoryDrawer() {
      return this.shouldShowAppShell;
    },

    userPicture() {
      return this.$auth?.user?.picture || null;
    },

    appLayoutCssVars() {
      return {
        '--app-header-height': `${APP_HEADER_HEIGHT}px`,
      };
    },

  },

  created() {
    if (this.$route.path.includes("/widgets/")) {
      this.app_bar_active = false;
    }
    // Hide drawer and app bar for public routes
    if (this.$route.path.includes("/unsubscribe") || this.$route.path.includes("/not_found")) {
      this.app_bar_active = false;
    }
  },

  mounted() {
    const instance = getInstance();
    instance.$watch("loading", async loading => {
      if (!loading && instance.isAuthenticated) {
        this.tool_bar_active = false;
        if (this.$route.path.includes("/widgets/")) {
          this.app_bar_active = false;
        }
        else {
          this.app_bar_active = true;
        }
        this.disable_nav = false;
        //this.drawer = true;
        this.authenticated = true;
        this.overlay = false;
        this.loadCurrentUser();
        this.$nextTick(() => {
          this.syncSearchHeaderState();
        });
      }
      else {
        this.tool_bar_active = true;
        this.app_bar_active = false;
        this.disable_nav = true;
        this.authenticated = false;
        this.right_drawer = false;
        this.closeAdvancedSearch();
        this.closeHelpDrawer();
        this.closeSearchHistoryDrawer();
      }

    });

    this.$nextTick(() => {
      this.handleDrawerOnRoute(this.$route, null);
      this.syncSearchHeaderState();
    });

    if (this.$route.path.includes("/widgets/")) {
      this.app_bar_active = false;
    }
    
    // Hide drawer and app bar for public routes
    if (this.$route.path.includes("/unsubscribe") || this.$route.path.includes("/not_found")) {
      this.app_bar_active = false;
    }

    bus.$on("hide-side-navs", hide => {
      ////////////console.log("HIDE SIDE NAVS");
      this.right_drawer = false;
      this.closeAdvancedSearch();
      this.closeHelpDrawer();
      this.closeSearchHistoryDrawer();
      this.closeSavedSearchesDrawer();
    });

    bus.$on("show_dialog", dialog => {
      ////////////console.log(`CALLED DIALOG :)` + dialog.msg  + " STATE: " + dialog.state);
      this.dialog_msg = dialog.msg;
      this.dialog_title = dialog.title;
      this.dialog = dialog.state;
    });

    bus.$on('intelligence-setup-update', ({ progress, showCard }) => {
      this.intelligenceSetupProgress = progress ?? 0;
      this.showIntelligenceSetupCard = !!showCard;
    });
  },

  methods: {
    closeSavedSearchesDrawer() {
      this.$refs.appShell?.closeSavedSearchesDrawer();
    },

    goToTraceyPage() {
      // "Ask Tracey" now navigates to the full /tracey page as a fresh new chat
      // instead of opening the side-chat drawer. Navigating to the base route
      // (no threadId) lands the user on a new chat; if already there, the
      // duplicate-navigation error is harmless and ignored.
      this.closeSavedSearchesDrawer();
      this.right_drawer = false;
      this.$router.push({ name: "tracey-chat" }).catch(() => {});
    },

    goToWhatsNew() {
      // "What's New" is always reachable from the top bar: jump to the home
      // dashboard and scroll to the What's New section. When already home we can
      // scroll immediately; otherwise navigate first and let the dashboard scroll
      // once it has mounted (it reads the `section` query on mount).
      this.closeSavedSearchesDrawer();
      this.right_drawer = false;
      if (this.$route.name === "home") {
        bus.$emit("scroll-to-whats-new");
      } else {
        this.$router
          .push({ name: "home", query: { section: "whats-new" } })
          .catch(() => {});
      }
    },

    toggleSearchHistoryDrawer() {
      this.closeSavedSearchesDrawer();
      this.$refs.searchHistoryDrawer?.toggle();
    },

    openUtilityDrawer(tabID) {
      this.closeSavedSearchesDrawer();
      this.closeSearchHistoryDrawer();

      if (tabID === 3) {
        this.right_drawer = false;
        this.isDrawerFullScreen = false;
        this.closeAdvancedSearch();
        this.$refs.helpDrawer?.toggle();
        this.current_tab = tabID;
        return;
      }

      this.closeHelpDrawer();
      this.closeAdvancedSearch();
      this.drawer_tabs = tabID;
      this.isDrawerFullScreen = false;
      this.showHide(tabID);
    },

    onSearchHeaderFiltersChanged(state) {
      this.searchHeaderState = state;
    },

    syncSearchHeaderState() {
      const state = this.$refs.advancedSearchDrawer?.getSearchHeaderState();
      if (state) {
        this.searchHeaderState = state;
      }
    },

    closeAdvancedSearch() {
      this.$refs.advancedSearchDrawer?.close();
    },

    closeHelpDrawer() {
      this.$refs.helpDrawer?.close();
    },

    closeSearchHistoryDrawer() {
      this.$refs.searchHistoryDrawer?.close();
    },

    onAdvancedSearchCloseUtilityDrawers() {
      this.right_drawer = false;
      this.isDrawerFullScreen = false;
      this.closeHelpDrawer();
      this.closeSearchHistoryDrawer();
      this.closeSavedSearchesDrawer();
    },

    onHelpCloseUtilityDrawers() {
      this.right_drawer = false;
      this.isDrawerFullScreen = false;
      this.closeSearchHistoryDrawer();
      this.closeSavedSearchesDrawer();
    },

    onSavedSearchesCloseUtilityDrawers() {
      this.right_drawer = false;
      this.isDrawerFullScreen = false;
      this.closeAdvancedSearch();
      this.closeHelpDrawer();
      this.closeSearchHistoryDrawer();
    },

    onSearchHistoryCloseUtilityDrawers() {
      this.right_drawer = false;
      this.isDrawerFullScreen = false;
      this.closeAdvancedSearch();
      this.closeHelpDrawer();
      this.closeSavedSearchesDrawer();
    },

    onContinueIntelligenceSetup() {
      if (isDashboardRoute(this.$route.name)) {
        bus.$emit('open-intelligence-setup');
        return;
      }
      this.$router.push({ name: 'home' }).then(() => {
        this.$nextTick(() => bus.$emit('open-intelligence-setup'));
      });
    },

    remove_competitor(item) {
      this.competitor_list.splice(
        this.competitor_list.indexOf(item),
        1
      );
      this.competitor_list = [...this.competitor_list];
    },

    async sendProductFeedback(productFeature) {
      this.currentURL = window.location.href;
      window.location.href = 'mailto:' + 'support@tendertrace.com?subject=Tendertrace Product Feedback&body=Product Feature: ' + productFeature + '%0D' + '%0DPlease enter your message below this line.%0D';
    },

    showHide(tabID) {
      if (!this.right_drawer) {
        //this.drawer = false;
        this.right_drawer = true;
      } else if (this.current_tab === tabID) {
        this.right_drawer = false;
        //this.drawer = true;
      } else {
        //this.drawer = false;
        this.right_drawer = true;
      }
      this.current_tab = tabID;
    },

    globalSearch(search_terms) {
      var query = search_terms;
      bus.$emit("global-search", query);
    },

    plaintextSearch(search_terms) {
      var query = search_terms;
      bus.$emit("plaintext-search", query);
    },

    refresh() {
      bus.$emit("refresh_lists");
    },

    disabled() {
      if (this.$auth.isAuthenticated) {
        this.disable_nav = false;
        return this.disable_nav;
      } else {
        this.disable_nav = true;
        return this.disable_nav;
      }
    },

    handleDrawerOnRoute(to, from) {
      if (to.path.includes("/unsubscribe") || to.path.includes("/not_found")) {
        this.app_bar_active = false;
      } else if (to.path.includes("/widgets/")) {
        this.app_bar_active = false;
      }

      this.$refs.advancedSearchDrawer?.handleRouteChange(to);
    },

    async loadCurrentUser() {
      try {
        const response = await getCurrentUserProfile();
        if (response.status === 'success') {
          this.currentUser = response.data;
          this.isAdmin = response.data.tt_admin === true;
        }
      } catch (error) {
        ////console.error('Error loading current user:', error);
        this.isAdmin = false;
      }
    }

  }
};
</script>

<style>
/* hide mobile version by default */
.logo .mobile {
  display: none;
}

@media (max-width: 600px) {
  .logo .mobile {
    display: block;
  }

  .logo .desktop {
    display: none;
  }

  .tab-menu {
    max-width: 200px;
  }

  .drawer {
    transition: all 0.2s ease-in-out;
  }
}

.fullscreen-drawer {
  /* top: 0 !important;
  bottom: 0 !important;
  right: 0 !important;
  left: auto !important;
  border-radius: 0 !important;
  z-index: 9999 !important; */
  transition: width 0.3s ease, margin 0.3s ease;
}

/* Profile panel: fixed header/footer, scrollable mid section */
.profile-tab-item {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.profile-panel-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.profile-panel-header {
  flex-shrink: 0;
}

.profile-panel-mid {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

</style>